import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getWhatsappStatus,
  listWhatsappMessages,
  listWhatsappTemplates,
  markWhatsappConversationRead,
  sendWhatsappTemplate,
  sendWhatsappText,
  setWhatsappConversationArchived,
  setWhatsappMessageRead,
  syncWhatsappTemplates,
} from "@/lib/whatsapp.functions";

import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { VoysCallButton } from "@/components/voys/VoysCallButton";
import { VoysCallHistory } from "@/components/voys/VoysCallHistory";

export const Route = createFileRoute("/_authenticated/admin/whatsapp")({
  validateSearch: (s: Record<string, unknown>): { phone?: string } => ({
    phone: typeof s.phone === "string" ? s.phone : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Admin — WhatsApp Business" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: WhatsappAdmin,
});

// Kleine helper: state die zichzelf bewaart in localStorage zodat zoekterm/filters
// een page reload overleven. Validator zorgt dat corrupte waarden vervangen worden
// door de default.
function usePersistedState<T>(
  key: string,
  initial: T,
  isValid: (v: unknown) => v is T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return initial;
      const parsed = JSON.parse(raw);
      return isValid(parsed) ? parsed : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* quota / private mode: negeren */
    }
  }, [key, value]);
  return [value, setValue];
}

function useRecentNumbers() {
  const [numbers, setNumbers] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem("wa-admin.recentNumbers");
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) return parsed;
      return [];
    } catch {
      return [];
    }
  });

  const add = useCallback((n: string) => {
    const clean = normalizeNumber(n);
    if (!clean) return;
    setNumbers((prev) => {
      const next = [clean, ...prev.filter((x) => x !== clean)].slice(0, 10);
      try { window.localStorage.setItem("wa-admin.recentNumbers", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const remove = useCallback((n: string) => {
    setNumbers((prev) => {
      const next = prev.filter((x) => x !== normalizeNumber(n));
      try { window.localStorage.setItem("wa-admin.recentNumbers", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  return { numbers, add, remove };
}

function WhatsappAdmin() {
  const { isAdmin, loading } = useAuth();
  const qc = useQueryClient();
  const { phone: phoneParam } = Route.useSearch();

  const statusFn = useServerFn(getWhatsappStatus);
  const listFn = useServerFn(listWhatsappMessages);
  const tplListFn = useServerFn(listWhatsappTemplates);
  const tplSyncFn = useServerFn(syncWhatsappTemplates);
  const sendTplFn = useServerFn(sendWhatsappTemplate);
  const sendTxtFn = useServerFn(sendWhatsappText);
  const markReadFn = useServerFn(markWhatsappConversationRead);
  const setMsgReadFn = useServerFn(setWhatsappMessageRead);
  const archiveFn = useServerFn(setWhatsappConversationArchived);


  const status = useQuery({
    queryKey: ["wa-status"],
    queryFn: () => statusFn({}),
    enabled: isAdmin,
  });

  const [rtStatus, setRtStatus] = useState<"connecting" | "live" | "polling">("connecting");
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);

  const [direction, setDirection] = usePersistedState<"all" | "inbound" | "outbound">(
    "wa-admin.direction",
    "all",
    (v): v is "all" | "inbound" | "outbound" => v === "all" || v === "inbound" || v === "outbound",
  );
  const [search, setSearch] = usePersistedState<string>(
    "wa-admin.search",
    "",
    (v): v is string => typeof v === "string",
  );
  const messages = useQuery({
    queryKey: ["wa-messages", direction, search],
    queryFn: () => listFn({ data: { direction, search: search || undefined, limit: 200 } }),
    enabled: isAdmin,
    // Vangnet: als realtime werkt pollen we traag (2 min), anders snel (10s).
    refetchInterval: rtStatus === "live" ? 120_000 : 10_000,
  });

  // Realtime: nieuwe whatsapp_messages meteen tonen + connectiestatus bijhouden
  useEffect(() => {
    if (!isAdmin) return;
    const channel = supabase
      .channel("wa-messages-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "whatsapp_messages" },
        () => {
          setLastEventAt(Date.now());
          qc.invalidateQueries({ queryKey: ["wa-messages"] });
        },
      )
      .subscribe((s) => {
        if (s === "SUBSCRIBED") setRtStatus("live");
        else if (s === "CHANNEL_ERROR" || s === "TIMED_OUT" || s === "CLOSED") setRtStatus("polling");
        else setRtStatus("connecting");
      });
    return () => {
      supabase.removeChannel(channel);
      setRtStatus("connecting");
    };
  }, [isAdmin, qc]);


  const templates = useQuery({
    queryKey: ["wa-templates"],
    queryFn: () => tplListFn({}),
    enabled: isAdmin,
  });

  const sync = useMutation({
    mutationFn: () => tplSyncFn({}),
    onSuccess: (r: any) => {
      toast.success(`${r.synced} templates gesynchroniseerd`);
      qc.invalidateQueries({ queryKey: ["wa-templates"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Sync mislukt"),
  });

  const [testTo, setTestTo] = useState("");
  const [testTpl, setTestTpl] = useState("");
  const [testLang, setTestLang] = useState("nl");
  const [testParams, setTestParams] = useState("");
  const [testText, setTestText] = useState("");
  const recent = useRecentNumbers();

  const sendTpl = useMutation({
    mutationFn: () =>
      sendTplFn({
        data: {
          to: testTo,
          templateName: testTpl,
          language: testLang || "nl",
          bodyParams: testParams
            ? testParams.split("|").map((s) => s.trim()).filter(Boolean)
            : undefined,
        },
      }),
    onSuccess: () => {
      toast.success("Template verstuurd");
      recent.add(testTo);
      qc.invalidateQueries({ queryKey: ["wa-messages"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Versturen mislukt"),
  });

  const sendTxt = useMutation({
    mutationFn: () =>
      sendTxtFn({ data: { to: testTo, body: testText } }),
    onSuccess: () => {
      toast.success("Bericht verstuurd");
      setTestText("");
      recent.add(testTo);
      qc.invalidateQueries({ queryKey: ["wa-messages"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Versturen mislukt"),
  });

  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>;

  const cfg = status.data?.configured;
  const webhookUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/api/public/hooks/whatsapp`;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">WhatsApp Business</h1>
          <p className="text-muted-foreground">Meta Cloud API — templates, verzending en inbox.</p>
        </div>
        <RealtimeBadge status={rtStatus} lastEventAt={lastEventAt} />
      </div>


      {/* Config status */}
      <div className="rounded-xl border bg-card p-5 space-y-3">
        <h2 className="font-semibold">Configuratie</h2>
        {status.isLoading ? (
          <p className="text-sm text-muted-foreground">Laden…</p>
        ) : (
          <ul className="text-sm space-y-1">
            <li>Access token: {cfg?.access_token ? "✅" : "❌ ontbreekt (WHATSAPP_ACCESS_TOKEN)"}</li>
            <li>Phone Number ID: {cfg?.phone_number_id ? "✅" : "❌ ontbreekt (WHATSAPP_PHONE_NUMBER_ID)"}</li>
            <li>Business Account ID: {cfg?.business_account_id ? "✅" : "❌ ontbreekt (WHATSAPP_BUSINESS_ACCOUNT_ID)"}</li>
            <li>Webhook verify token: {cfg?.webhook_verify_token ? "✅" : "❌"}</li>
            <li>App secret (webhook signatuur): {cfg?.app_secret ? "✅" : "❌ ontbreekt (WHATSAPP_APP_SECRET)"}</li>
          </ul>
        )}
        {status.data?.phoneInfo && (
          <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
            {JSON.stringify(status.data.phoneInfo, null, 2)}
          </pre>
        )}
        <div className="text-sm">
          <div className="font-medium">Webhook-URL</div>
          <code className="text-xs break-all">{webhookUrl}</code>
        </div>
      </div>

      {/* Templates */}
      <div className="rounded-xl border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Templates</h2>
          <button
            className="px-3 py-1.5 text-sm rounded bg-primary text-primary-foreground disabled:opacity-50"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
          >
            {sync.isPending ? "Sync…" : "Sync vanuit Meta"}
          </button>
        </div>
        {templates.isLoading ? (
          <p className="text-sm text-muted-foreground">Laden…</p>
        ) : (templates.data?.rows.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nog geen templates gesynced. Maak eerst een template aan in Meta Business Manager en klik daarna op "Sync vanuit Meta".
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Naam</th>
                <th>Taal</th>
                <th>Categorie</th>
                <th>Status</th>
                <th>Body</th>
              </tr>
            </thead>
            <tbody>
              {templates.data!.rows.map((t: any) => (
                <tr key={t.id} className="border-t">
                  <td className="py-1 font-mono">{t.name}</td>
                  <td>{t.language}</td>
                  <td>{t.category}</td>
                  <td>{t.status}</td>
                  <td className="truncate max-w-[400px]">{t.body}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Test send */}
      <div className="rounded-xl border bg-card p-5 space-y-3">
        <h2 className="font-semibold">Testbericht versturen</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="text-sm">
            Naar (E.164, bv. +31612345678)
            <input className="mt-1 w-full border rounded px-2 py-1" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          </label>
          <div />
          <label className="text-sm">
            Template-naam
            <input className="mt-1 w-full border rounded px-2 py-1" value={testTpl} onChange={(e) => setTestTpl(e.target.value)} placeholder="hello_world" />
          </label>
          <label className="text-sm">
            Taal
            <input className="mt-1 w-full border rounded px-2 py-1" value={testLang} onChange={(e) => setTestLang(e.target.value)} />
          </label>
          <label className="text-sm md:col-span-2">
            Body-parameters ({"{{1}}"}, {"{{2}}"}…) — scheid met |
            <input className="mt-1 w-full border rounded px-2 py-1" value={testParams} onChange={(e) => setTestParams(e.target.value)} placeholder="Jan | Elvion" />
          </label>
        </div>
        <button
          className="px-3 py-1.5 text-sm rounded bg-primary text-primary-foreground disabled:opacity-50"
          onClick={() => sendTpl.mutate()}
          disabled={sendTpl.isPending || !testTo || !testTpl}
        >
          {sendTpl.isPending ? "Versturen…" : "Verstuur template"}
        </button>

        <div className="pt-4 border-t space-y-2">
          <label className="text-sm block">
            Vrij tekstbericht (alleen mogelijk binnen 24u nadat klant zelf een bericht stuurde)
            <textarea className="mt-1 w-full border rounded px-2 py-1" rows={2} value={testText} onChange={(e) => setTestText(e.target.value)} />
          </label>
          <button
            className="px-3 py-1.5 text-sm rounded border"
            onClick={() => sendTxt.mutate()}
            disabled={sendTxt.isPending || !testTo || !testText}
          >
            {sendTxt.isPending ? "Versturen…" : "Verstuur tekst"}
          </button>
        </div>
      </div>

      {/* Conversations + thread */}
      <ConversationsPanel
        rows={messages.data?.rows ?? []}
        customersByPhone={messages.data?.customersByPhone ?? {}}
        ordersByPhone={messages.data?.ordersByPhone ?? {}}
        archivedByPhone={messages.data?.archivedByPhone ?? {}}


        loading={messages.isLoading}
        direction={direction}
        setDirection={setDirection}
        search={search}
        setSearch={setSearch}
        initialPhone={phoneParam}
        onReply={(to, body) => sendTxtFn({ data: { to, body } }).then(() => {
          recent.add(to);
          qc.invalidateQueries({ queryKey: ["wa-messages"] });
        })}
        onOpenConversation={async (phone) => {
          try {
            const res = await markReadFn({ data: { phone } });
            if (res.updated > 0) qc.invalidateQueries({ queryKey: ["wa-messages"] });
          } catch { /* stil laten falen */ }
        }}
        onToggleMessageRead={async (id, read) => {
          try {
            await setMsgReadFn({ data: { id, read } });
            qc.invalidateQueries({ queryKey: ["wa-messages"] });
          } catch (e: any) {
            toast.error(e?.message ?? "Bijwerken mislukt");
          }
        }}
        onToggleArchived={async (phone, archived) => {
          try {
            await archiveFn({ data: { phone, archived } });
            toast.success(archived ? "Gesprek gearchiveerd" : "Gesprek heropend");
            qc.invalidateQueries({ queryKey: ["wa-messages"] });
          } catch (e: any) {
            toast.error(e?.message ?? "Archiveren mislukt");
          }
        }}
      />

      {/* Nieuw gesprek starten via los nummer — ingeklapt, weinig gebruikt */}
      <QuickWhatsappLauncher />

    </div>
  );
}

/* ---------------------- Quick launcher (nieuw nummer) ---------------------- */

function QuickWhatsappLauncher() {
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState("");
  const digits = phone.replace(/[^\d]/g, "");
  const valid = digits.length >= 8;
  const waHref = valid
    ? `https://wa.me/${digits}${msg.trim() ? `?text=${encodeURIComponent(msg.trim())}` : ""}`
    : "#";
  const inboxHref = valid ? `/admin/whatsapp?phone=${encodeURIComponent(digits)}` : "#";
  const recent = useRecentNumbers();

  return (
    <details className="rounded-xl border bg-card">
      <summary className="cursor-pointer select-none px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
        📱 Nieuw nummer starten of los telefoonnummer aanklikken
      </summary>
      <div className="px-4 py-3 border-t space-y-3">
        <p className="text-xs text-muted-foreground">
          Voer een telefoonnummer in (met of zonder +31 / 0031 / 0) om direct WhatsApp Web te openen of
          het bijbehorende gesprek in de inbox te selecteren. Losse tekst hier stuurt géén bericht via de Cloud API —
          voor bulk of templates gebruik je de blokken hierboven.
        </p>
        {recent.numbers.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[11px] text-muted-foreground">Recente nummers</div>
            <div className="flex flex-wrap gap-1.5">
              {recent.numbers.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPhone(n)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-background hover:bg-muted text-xs"
                  title={`+${n} — klik om in te vullen`}
                >
                  <span>+{n}</span>
                  <span
                    className="text-muted-foreground hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); recent.remove(n); }}
                    title="Verwijder uit recent"
                  >
                    ×
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-[1fr_2fr] gap-3">
          <label className="text-sm">
            Telefoonnummer
            <input
              className="mt-1 w-full border rounded px-2 py-1"
              placeholder="+31 6 12 34 56 78"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              autoComplete="off"
            />
            {phone && !valid && (
              <span className="text-[11px] text-amber-600">Minimaal 8 cijfers vereist.</span>
            )}
            {valid && (
              <span className="text-[11px] text-muted-foreground">Wordt gebeld/gestuurd naar: +{digits}</span>
            )}
          </label>
          <label className="text-sm">
            Optioneel voorbericht (alleen voor WhatsApp Web)
            <textarea
              className="mt-1 w-full border rounded px-2 py-1"
              rows={2}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Hoi, ..."
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { if (!valid) e.preventDefault(); }}
            className={`px-3 py-1.5 text-sm rounded ${valid ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-muted text-muted-foreground cursor-not-allowed"}`}
          >
            💬 Open in WhatsApp Web
          </a>
          <Link
            to="/admin/whatsapp"
            search={valid ? { phone: digits } : undefined}
            className={`px-3 py-1.5 text-sm rounded border ${valid ? "bg-background hover:bg-muted" : "bg-muted text-muted-foreground pointer-events-none"}`}
          >
            📥 Ga naar gesprek in inbox
          </Link>
          {valid && (
            <>
              <a
                href={`tel:+${digits}`}
                className="px-3 py-1.5 text-sm rounded border bg-background hover:bg-muted"
              >
                📞 tel:
              </a>
              <VoysCallButton phone={`+${digits}`} label="Voys" size="md" />
            </>
          )}
        </div>
      </div>
    </details>
  );
}


/* ---------------------- Conversations UI ---------------------- */

type WaRow = {
  id: string;
  created_at: string;
  direction: "inbound" | "outbound";
  from_number: string | null;
  to_number: string | null;
  body: string | null;
  message_type: string | null;
  template_name: string | null;
  status: string | null;
  error_message: string | null;
  read_at: string | null;
  read_by: string | null;
  read_by_name?: string | null;
};


function normalizeNumber(n: string | null | undefined, defaultCountry = "31"): string {
  if (!n) return "";
  const raw = String(n).trim();
  // Behoud een leidende + zodat we internationale notatie kunnen herkennen
  const hasPlus = raw.startsWith("+");
  let digits = raw.replace(/[^\d]/g, "");
  if (!digits) return "";
  // 00-prefix (bijv. 0031...) → internationale notatie
  if (!hasPlus && digits.startsWith("00")) {
    digits = digits.slice(2);
  } else if (hasPlus) {
    // + betekent dat digits al de landcode bevat
  } else if (digits.startsWith("0")) {
    // Nationaal nummer met leidende 0 → vervang door landcode
    digits = defaultCountry + digits.replace(/^0+/, "");
  } else if (digits.length > 0 && digits.length <= 10) {
    // Kort nummer zonder landcode → veronderstel default land
    digits = defaultCountry + digits;
  }
  return digits;
}

type CustomerInfo = { id: string; name: string; customer_number: string | null };
type OrderInfo = {
  id: string;
  order_number: string | null;
  status: string | null;
  total_cents: number | null;
  created_at: string;
  payment_status: string | null;
};
type ArchiveInfo = {
  archived_at: string;
  archived_by: string | null;
  archived_by_name: string | null;
  note: string | null;
};

function ConversationsPanel({
  rows,
  customersByPhone = {},
  ordersByPhone = {},
  archivedByPhone = {},
  loading,
  direction,
  setDirection,
  search,
  setSearch,
  initialPhone,
  onReply,
  onOpenConversation,
  onToggleMessageRead,
  onToggleArchived,
}: {
  rows: WaRow[];
  customersByPhone?: Record<string, CustomerInfo>;
  ordersByPhone?: Record<string, OrderInfo[]>;
  archivedByPhone?: Record<string, ArchiveInfo>;
  loading: boolean;
  direction: "all" | "inbound" | "outbound";
  setDirection: (v: "all" | "inbound" | "outbound") => void;
  search: string;
  setSearch: (v: string) => void;
  initialPhone?: string;
  onReply: (to: string, body: string) => Promise<void>;
  onOpenConversation?: (phone: string) => void | Promise<void>;
  onToggleMessageRead?: (id: string, read: boolean) => void | Promise<void>;
  onToggleArchived?: (phone: string, archived: boolean) => void | Promise<void>;
}) {



  const recent = useRecentNumbers();

  const [sortOrder, setSortOrder] = usePersistedState<"newest" | "oldest">(
    "wa-admin.sortOrder",
    "newest",
    (v): v is "newest" | "oldest" => v === "newest" || v === "oldest",
  );

  const conversations = useMemo(() => {
    const map = new Map<string, { key: string; display: string; last: WaRow; messages: WaRow[]; unread: number }>();
    for (const m of rows) {
      const counterpart = m.direction === "outbound" ? m.to_number : m.from_number;
      const key = normalizeNumber(counterpart);
      if (!key) continue;
      const isUnread = m.direction === "inbound" && !m.read_at;
      const existing = map.get(key);
      if (existing) {
        existing.messages.push(m);
        if (isUnread) existing.unread += 1;
        if (new Date(m.created_at) > new Date(existing.last.created_at)) existing.last = m;
      } else {
        map.set(key, { key, display: counterpart ?? key, last: m, messages: [m], unread: isUnread ? 1 : 0 });
      }
    }
    const list = Array.from(map.values());
    list.sort((a, b) => {
      if ((b.unread > 0 ? 1 : 0) !== (a.unread > 0 ? 1 : 0)) return (b.unread > 0 ? 1 : 0) - (a.unread > 0 ? 1 : 0);
      const aTime = new Date(a.last.created_at).getTime();
      const bTime = new Date(b.last.created_at).getTime();
      return sortOrder === "newest" ? bTime - aTime : aTime - bTime;
    });
    for (const c of list) {
      c.messages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
    return list;
  }, [rows, sortOrder]);

  const [linkFilter, setLinkFilter] = usePersistedState<"all" | "linked" | "unlinked">(
    "wa-admin.linkFilter",
    "all",
    (v): v is "all" | "linked" | "unlinked" => v === "all" || v === "linked" || v === "unlinked",
  );
  const [unreadOnly, setUnreadOnly] = usePersistedState<boolean>(
    "wa-admin.unreadOnly",
    false,
    (v): v is boolean => typeof v === "boolean",
  );
  const [archiveFilter, setArchiveFilter] = usePersistedState<"active" | "archived" | "all">(
    "wa-admin.archiveFilter",
    "active",
    (v): v is "active" | "archived" | "all" => v === "active" || v === "archived" || v === "all",
  );

  const filteredConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qDigits = q.replace(/[^\d]/g, "");
    return conversations.filter((c) => {
      const cust = customersByPhone[c.key];
      const isArchived = !!archivedByPhone[c.key];
      if (archiveFilter === "active" && isArchived) return false;
      if (archiveFilter === "archived" && !isArchived) return false;
      if (linkFilter === "linked" && !cust) return false;
      if (linkFilter === "unlinked" && cust) return false;
      if (unreadOnly && c.unread === 0) return false;
      if (!q) return true;
      const hay = [
        c.display,
        c.key,
        cust?.name ?? "",
        cust?.customer_number ?? "",
        c.last.body ?? "",
        c.last.template_name ?? "",
      ]
        .join(" ")
        .toLowerCase();
      if (hay.includes(q)) return true;
      if (qDigits && c.key.includes(qDigits)) return true;
      return false;
    });
  }, [conversations, customersByPhone, archivedByPhone, search, linkFilter, unreadOnly, archiveFilter]);

  // Lazy loading: toon eerst een beperkte batch en laad meer bij scrollen.
  const PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, linkFilter, unreadOnly, archiveFilter, direction]);
  const visibleConversations = useMemo(
    () => filteredConversations.slice(0, visibleCount),
    [filteredConversations, visibleCount],
  );
  const hasMore = visibleCount < filteredConversations.length;
  const listRef = useRef<HTMLUListElement | null>(null);
  const sentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (!hasMore) return;
    const root = listRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((n) => Math.min(n + PAGE_SIZE, filteredConversations.length));
        }
      },
      { root, rootMargin: "200px" },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [hasMore, filteredConversations.length]);

  const totalUnread = conversations.reduce(
    (sum, c) => sum + (archivedByPhone[c.key] ? 0 : c.unread),
    0,
  );
  const linkedCount = conversations.reduce((n, c) => n + (customersByPhone[c.key] ? 1 : 0), 0);
  const archivedCount = conversations.reduce((n, c) => n + (archivedByPhone[c.key] ? 1 : 0), 0);
  const activeCount = conversations.length - archivedCount;


  const initialKey = initialPhone ? normalizeNumber(initialPhone) : null;
  const [selectedKey, setSelectedKey] = useState<string | null>(initialKey);

  const openConversation = (key: string, display: string) => {
    setSelectedKey(key);
    recent.add(display || key);
    if (onOpenConversation) void onOpenConversation(display || key);
  };

  // Volg wijzigingen van de ?phone= parameter (bv. navigatie vanuit planning).
  useEffect(() => {
    if (initialKey) {
      setSelectedKey(initialKey);
      if (onOpenConversation) void onOpenConversation(initialPhone ?? initialKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  // Zodra een geselecteerd gesprek nog ongelezen berichten heeft (bv. realtime binnenkomer), markeer ze meteen.
  useEffect(() => {
    if (!selectedKey) return;
    const current = conversations.find((c) => c.key === selectedKey);
    if (current && current.unread > 0 && onOpenConversation) {
      void onOpenConversation(current.display);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, conversations]);

  useEffect(() => {
    if (!selectedKey && conversations[0]) {
      const first = conversations[0];
      setSelectedKey(first.key);
      if (onOpenConversation && first.unread > 0) void onOpenConversation(first.display);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, selectedKey]);

  const selected =
    conversations.find((c) => c.key === selectedKey)
    ?? (initialKey
      ? { key: initialKey, display: initialPhone ?? initialKey, last: null as any, messages: [] as WaRow[], unread: 0 }
      : null);

  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 flex-wrap p-4 border-b">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold">Gesprekken</h2>
          {totalUnread > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-semibold">
              {totalUnread}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            · {filteredConversations.length}/{conversations.length}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-md border overflow-hidden text-xs">
            {(["active", "archived", "all"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setArchiveFilter(k)}
                className={`px-2 py-1 ${archiveFilter === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                title={k === "archived" ? "Toon afgeronde/gearchiveerde gesprekken" : k === "active" ? "Toon actieve gesprekken" : "Toon alles"}
              >
                {k === "active" ? `Actief (${activeCount})` : k === "archived" ? `📥 Archief (${archivedCount})` : `Alle (${conversations.length})`}
              </button>
            ))}
          </div>
          <div className="flex rounded-md border overflow-hidden text-xs">
            {(["all", "linked", "unlinked"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setLinkFilter(k)}
                className={`px-2 py-1 ${linkFilter === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              >
                {k === "all" ? `Alle (${conversations.length})` : k === "linked" ? `Klant (${linkedCount})` : `Onbekend (${conversations.length - linkedCount})`}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setUnreadOnly((v) => !v)}
            className={`px-2 py-1 text-xs rounded-md border ${unreadOnly ? "bg-red-600 text-white border-red-600" : "bg-background hover:bg-muted"}`}
          >
            Alleen ongelezen{totalUnread > 0 ? ` (${totalUnread})` : ""}
          </button>
          <select className="border rounded px-2 py-1 text-sm" value={direction} onChange={(e) => setDirection(e.target.value as any)}>
            <option value="all">Alle richtingen</option>
            <option value="outbound">Uitgaand</option>
            <option value="inbound">Inkomend</option>
          </select>
          <select
            className="border rounded px-2 py-1 text-sm"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "newest" | "oldest")}
            title="Sorteer op laatste bericht"
          >
            <option value="newest">Nieuwste eerst</option>
            <option value="oldest">Oudste eerst</option>
          </select>
          <div className="relative">
            <input
              className="border rounded pl-2 pr-6 py-1 text-sm w-56"
              placeholder="Naam, klantnr, nummer of tekst"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                aria-label="Wissen"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground p-4">Laden…</p>
      ) : conversations.length === 0 ? (
        <p className="text-sm text-muted-foreground p-4">Nog geen berichten.</p>
      ) : filteredConversations.length === 0 ? (
        <p className="text-sm text-muted-foreground p-4">Geen gesprekken die aan de filters voldoen.</p>
      ) : (
        <div className="grid md:grid-cols-[280px_1fr] min-h-[520px]">
          {/* Conversation list */}
          <ul ref={listRef} className="border-r divide-y max-h-[70vh] overflow-y-auto">
            {visibleConversations.map((c) => {

              const active = c.key === selectedKey;
              const previewBody = c.last.body ?? (c.last.template_name ? `tpl: ${c.last.template_name}` : c.last.message_type ?? "");
              const hasUnread = c.unread > 0;
              const cust = customersByPhone[c.key];
              const arch = archivedByPhone[c.key];
              return (
                <li key={c.key}>
                  <button
                    type="button"
                    onClick={() => openConversation(c.key, c.display)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-muted/60 ${active ? "bg-muted" : ""} ${hasUnread && !active ? "bg-emerald-50/60" : ""} ${arch ? "opacity-70" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className={`text-sm truncate flex items-center gap-1.5 ${hasUnread ? "font-semibold" : "font-medium"}`}>
                        {arch && <span title="Gearchiveerd" className="text-[10px]">📥</span>}
                        <span className="truncate">{cust?.name || c.display}</span>
                      </div>
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        {hasUnread && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold">
                            {c.unread}
                          </span>
                        )}
                        <div className="text-[10px] text-muted-foreground">
                          {new Date(c.last.created_at).toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" })}
                        </div>
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {cust ? (
                        <>
                          <a
                            href={`https://wa.me/${c.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open in WhatsApp Web"
                            className="hover:text-foreground hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {c.display}
                          </a>
                          <a
                            href={`tel:+${c.key}`}
                            title="Bellen (tel:)"
                            className="ml-1 hover:text-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📞
                          </a>
                          <span onClick={(e) => e.stopPropagation()} className="ml-1 inline-block">
                            <VoysCallButton phone={`+${c.key}`} size="xs" showLabel={false} variant="ghost" />
                          </span>
                          {cust.customer_number && <span className="ml-1 opacity-70">· {cust.customer_number}</span>}
                        </>
                      ) : (
                        <>
                          <a
                            href={`https://wa.me/${c.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open in WhatsApp Web"
                            className="italic opacity-70 hover:text-foreground hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {c.display}
                          </a>
                          <a
                            href={`tel:+${c.key}`}
                            title="Bellen (tel:)"
                            className="ml-1 hover:text-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📞
                          </a>
                          <span onClick={(e) => e.stopPropagation()} className="ml-1 inline-block">
                            <VoysCallButton phone={`+${c.key}`} size="xs" showLabel={false} variant="ghost" />
                          </span>
                        </>
                      )}
                    </div>
                    <div className={`text-xs truncate ${hasUnread ? "text-foreground" : "text-muted-foreground"}`}>
                      {c.last.direction === "outbound" ? "➡️ " : "⬅️ "}
                      {previewBody}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {c.messages.length} bericht{c.messages.length === 1 ? "" : "en"}
                      {hasUnread && <span className="ml-1 text-red-700">· {c.unread} nieuw</span>}
                      {arch && (
                        <span className="ml-1 text-amber-700">
                          · afgerond {new Date(arch.archived_at).toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" })}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
            {hasMore && (
              <li ref={sentinelRef} className="px-3 py-3 text-center text-[11px] text-muted-foreground">
                <button
                  type="button"
                  onClick={() => setVisibleCount((n) => Math.min(n + PAGE_SIZE, filteredConversations.length))}
                  className="hover:underline"
                >
                  Meer laden… ({filteredConversations.length - visibleCount} resterend)
                </button>
              </li>
            )}
            {!hasMore && filteredConversations.length > PAGE_SIZE && (
              <li className="px-3 py-2 text-center text-[10px] text-muted-foreground opacity-70">
                Einde lijst · {filteredConversations.length} gesprekken
              </li>
            )}
          </ul>


          {/* Thread */}
          <div className="flex flex-col max-h-[70vh]">
            {selected ? (
              <>
                {(() => {
                  const arch = archivedByPhone[selected.key] ?? null;
                  if (!onToggleArchived && !arch) return null;
                  return (
                    <div className={`px-4 py-2 border-b flex items-center justify-between gap-3 text-xs ${arch ? "bg-amber-50 dark:bg-amber-950/30" : ""}`}>
                      <div className="min-w-0">
                        {arch ? (
                          <span className="text-amber-900 dark:text-amber-200 truncate inline-flex items-center gap-1.5">
                            <span>📥</span>
                            <span>
                              Afgerond op{" "}
                              {new Date(arch.archived_at).toLocaleString("nl-NL", {
                                day: "2-digit", month: "2-digit", year: "2-digit",
                                hour: "2-digit", minute: "2-digit",
                              })}
                              {arch.archived_by_name ? ` door ${arch.archived_by_name}` : ""}
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Actief gesprek</span>
                        )}
                      </div>
                      {onToggleArchived && (
                        <button
                          type="button"
                          onClick={() => onToggleArchived(selected.display || selected.key, !arch)}
                          className={`px-2 py-1 rounded border ${arch ? "bg-background hover:bg-muted" : "bg-amber-600 text-white border-amber-600 hover:bg-amber-700"}`}
                        >
                          {arch ? "↩️ Heropenen" : "✅ Markeer als afgerond"}
                        </button>
                      )}
                    </div>
                  );
                })()}
                <ThreadContextHeader
                  display={selected.display}
                  messageCount={selected.messages.length}
                  customer={customersByPhone[selected.key] ?? null}
                  orders={ordersByPhone[selected.key] ?? []}
                />



                <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-muted/20">
                  {selected.messages.length === 0 ? (
                    <div className="text-center text-xs text-muted-foreground py-8">
                      Geen berichten met dit nummer. Start hieronder een gesprek (alleen mogelijk als de klant je binnen 24u een bericht stuurde, anders via een template).
                    </div>
                  ) : selected.messages.map((m) => {
                    const mine = m.direction === "outbound";
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                            mine ? "bg-primary text-primary-foreground" : "bg-background border"
                          }`}
                        >
                          <div className="whitespace-pre-wrap break-words">
                            {m.body ?? (m.template_name ? `[template: ${m.template_name}]` : `[${m.message_type ?? "bericht"}]`)}
                          </div>
                          <div className={`mt-1 text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                            {new Date(m.created_at).toLocaleString("nl-NL")}
                            {m.status && ` · ${m.status}`}
                          </div>
                          {m.error_message && <div className="mt-1 text-[10px] text-red-500">{m.error_message}</div>}
                          {!mine && (
                            <div className="mt-1 flex items-center justify-between gap-2 text-[10px]">
                              {m.read_at ? (
                                <span className="inline-flex items-center gap-1 text-emerald-700">
                                  <span aria-hidden>✓✓</span>
                                  <span>
                                    Gelezen door {m.read_by_name || "medewerker"} ·{" "}
                                    {new Date(m.read_at).toLocaleString("nl-NL", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-red-600">
                                  <span className="h-1.5 w-1.5 rounded-full bg-red-600" />
                                  Ongelezen
                                </span>
                              )}
                              {onToggleMessageRead && (
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                                  onClick={() => onToggleMessageRead(m.id, !m.read_at)}
                                >
                                  {m.read_at ? "markeer ongelezen" : "markeer gelezen"}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                    );
                  })}
                </div>
                {(() => {
                  const WINDOW_MS = 24 * 60 * 60 * 1000;
                  const lastInbound = [...selected.messages]
                    .reverse()
                    .find((m) => m.direction === "inbound");
                  const lastInboundAt = lastInbound ? new Date(lastInbound.created_at).getTime() : null;
                  const remaining = lastInboundAt ? WINDOW_MS - (Date.now() - lastInboundAt) : -1;
                  const inWindow = remaining > 0;
                  const hoursLeft = inWindow ? Math.floor(remaining / (60 * 60 * 1000)) : 0;
                  const minsLeft = inWindow ? Math.floor((remaining % (60 * 60 * 1000)) / 60_000) : 0;

                  if (!inWindow) {
                    return (
                      <div className="border-t p-3 bg-amber-50 dark:bg-amber-950/30">
                        <div className="flex items-start gap-2 text-xs">
                          <span className="text-amber-600 text-base leading-none">⚠️</span>
                          <div className="space-y-1">
                            <div className="font-semibold text-amber-900 dark:text-amber-200">
                              Buiten 24u-venster — vrije tekst niet toegestaan
                            </div>
                            <div className="text-amber-800/90 dark:text-amber-200/80">
                              {lastInboundAt
                                ? `Laatste bericht van klant: ${new Date(lastInboundAt).toLocaleString("nl-NL")}. `
                                : "Deze klant heeft nog geen bericht gestuurd. "}
                              Meta staat alleen goedgekeurde <span className="font-medium">templates</span> toe om opnieuw contact op te nemen. Scroll naar boven en gebruik "Verstuur template".
                            </div>
                          </div>
                        </div>
                        <input
                          className="mt-2 w-full border rounded px-3 py-2 text-sm bg-muted/50 text-muted-foreground cursor-not-allowed"
                          placeholder="Geblokkeerd — gebruik een template"
                          disabled
                          readOnly
                        />
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="border-t px-3 pt-2 pb-1 text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        24u-venster actief — nog {hoursLeft}u {minsLeft}m vrije tekst mogelijk
                      </div>
                      <form
                        className="p-3 flex gap-2"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          if (!reply.trim() || !selected) return;
                          setSending(true);
                          try {
                            const last = selected.last;
                            const to = last
                              ? (last.direction === "outbound" ? (last.to_number ?? selected.display) : (last.from_number ?? selected.display))
                              : selected.display;
                            await onReply(to, reply.trim());
                            setReply("");
                          } catch (err: any) {
                            toast.error(err?.message ?? "Versturen mislukt");
                          } finally {
                            setSending(false);
                          }
                        }}
                      >
                        <input
                          className="flex-1 border rounded px-3 py-2 text-sm"
                          placeholder="Antwoord (binnen 24u-venster)"
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          disabled={sending}
                        />
                        <button
                          type="submit"
                          className="px-3 py-2 text-sm rounded bg-primary text-primary-foreground disabled:opacity-50"
                          disabled={sending || !reply.trim()}
                        >
                          {sending ? "…" : "Verstuur"}
                        </button>
                      </form>
                    </>
                  );
                })()}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                Selecteer een gesprek
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RealtimeBadge({
  status,
  lastEventAt,
}: {
  status: "connecting" | "live" | "polling";
  lastEventAt: number | null;
}) {
  const cfg =
    status === "live"
      ? { dot: "bg-emerald-500 animate-pulse", label: "Realtime actief", cls: "border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10" }
      : status === "connecting"
        ? { dot: "bg-amber-500 animate-pulse", label: "Verbinden…", cls: "border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/10" }
        : { dot: "bg-muted-foreground", label: "Terugval op polling (10s)", cls: "border-border text-muted-foreground bg-muted" };
  const rel = lastEventAt
    ? `${Math.max(1, Math.round((Date.now() - lastEventAt) / 1000))}s geleden`
    : "nog geen events";
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${cfg.cls}`}
      title={`Laatste realtime event: ${rel}`}
    >
      <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
      <span className="font-medium">{cfg.label}</span>
      <span className="opacity-70">· {rel}</span>
    </div>
  );
}

function euro(cents: number | null): string {
  if (cents == null) return "—";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(cents / 100);
}

function ThreadContextHeader({
  display,
  messageCount,
  customer,
  orders,
}: {
  display: string;
  messageCount: number;
  customer: CustomerInfo | null;
  orders: OrderInfo[];
}) {
  return (
    <div className="border-b px-4 py-3 space-y-2 bg-muted/30">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium truncate">{customer?.name || display}</div>
          <div className="text-xs text-muted-foreground truncate">
            {customer ? (
              <>
                <a
                  href={`https://wa.me/${normalizeNumber(display)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in WhatsApp Web"
                  className="hover:text-foreground hover:underline"
                >
                  {display}
                </a>
                <a
                  href={`tel:+${normalizeNumber(display)}`}
                  title="Bellen (tel:)"
                  className="ml-1 hover:text-foreground"
                >
                  📞
                </a>
                <span className="ml-1 inline-block">
                  <VoysCallButton phone={`+${normalizeNumber(display)}`} threadKey={normalizeNumber(display)} size="xs" showLabel={false} variant="ghost" />
                </span>
                {customer.customer_number && <span className="ml-1 opacity-70">· {customer.customer_number}</span>}
                {" · "}
              </>
            ) : (
              <>
                <a
                  href={`https://wa.me/${normalizeNumber(display)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in WhatsApp Web"
                  className="hover:text-foreground hover:underline"
                >
                  {display}
                </a>
                <a
                  href={`tel:+${normalizeNumber(display)}`}
                  title="Bellen (tel:)"
                  className="ml-1 hover:text-foreground"
                >
                  📞
                </a>
                <span className="ml-1 inline-block">
                  <VoysCallButton phone={`+${normalizeNumber(display)}`} threadKey={normalizeNumber(display)} size="xs" showLabel={false} variant="ghost" />
                </span>
                {" · "}
              </>
            )}
            {messageCount} bericht{messageCount === 1 ? "" : "en"}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {customer ? (
            <Link
              to="/admin/klanten"
              className="px-2 py-1 rounded border bg-background hover:bg-muted"
              title="Open klantoverzicht"
            >
              👤 Klantkaart
            </Link>
          ) : (
            <span className="px-2 py-1 rounded border bg-background text-muted-foreground italic">
              Geen klant gekoppeld
            </span>
          )}
          <Link
            to="/admin/orders"
            className="px-2 py-1 rounded border bg-background hover:bg-muted"
            title="Open bestellingen"
          >
            📦 Bestellingen
          </Link>
        </div>
      </div>

      <VoysCallHistory
        phone={`+${normalizeNumber(display)}`}
        threadKey={normalizeNumber(display)}
        limit={5}
        className="pt-1"
      />



      {orders.length > 0 && (
        <div className="pt-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Recente orders ({orders.length})
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {orders.map((o) => (
              <li key={o.id}>
                <Link
                  to="/admin/orders"
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border bg-background hover:bg-muted text-[11px]"
                  title={`Order ${o.order_number ?? o.id.slice(0, 8)} · ${o.status ?? "?"} · ${o.payment_status ?? ""}`}
                >
                  <span className="font-medium">#{o.order_number ?? o.id.slice(0, 6)}</span>
                  <span className="text-muted-foreground">
                    {new Date(o.created_at).toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                  </span>
                  <span className="text-muted-foreground">· {euro(o.total_cents)}</span>
                  {o.status && (
                    <span className="px-1 rounded bg-muted text-[10px]">{o.status}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


